<template>
    <div class="card">
        <div class="card-header">
            Summary
            <br/>
            <small>Summary of the invoice</small>
        </div>
        <div class="card-body">
            <table v-if="company" class="table companyInformation">
                <tbody>
                <tr>
                    <td><b>{{ company.name }}</b></td>
                    <td class="text-right">{{ company.address_line_1 }}, {{
                        company.address_line_2
                        }}
                    </td>
                </tr>
                <tr>
                    <td>{{ company.organization_number }}</td>
                    <td class="text-right">{{ company.zip_code }}, {{ company.city }}</td>
                </tr>
                <tr>
                    <td>{{ company.vat_number }}</td>
                    <td class="text-right">{{ company.country }}</td>
                </tr>
                <tr>
                    <td>{{ company.contact_person }}</td>
                    <td class="text-right"></td>
                </tr>
                </tbody>
            </table>
            <hr/>
            <div class="d-flex justify-content-between flex-lg-wrap flex-xl-nowrap" style="gap: 15px">
              <h4 class="total-container mb-0" style="border: 2px solid #262b42; background: transparent;">
                <div class="d-flex align-items-center flex-wrap h-100 text-left text-readable" style="font-size: .9rem;">
                  <div class="d-flex justify-content-between align-items-center mb-2 w-100 flex-grow-1 flex-wrap">
                    <b class="mr-2">Netto</b><span>{{ netto | formatCurrency(invoice.currency)}}</span>
                  </div>
                  <div class="text-muted d-flex justify-content-between align-items-center w-100 flex-grow-1 flex-wrap">
                    <b class="mr-2">VAT</b><span>{{ vat | formatCurrency(invoice.currency)}}</span>
                  </div>
                </div>
              </h4>
              <h4 class="total-container mb-0">
                <div class="text-right px-2">
                  <small class="text-center">Brutto</small>
                  <br/>
                  <div>{{ brutto | formatCurrency(invoice.currency) }}</div>
                </div>
              </h4>
            </div>
            <h4 class="estimated-revenue-title" v-if="!!salary.total">
              <div class="d-flex justify-content-between align-items-center flex-wrap px-3" style="min-height: 5rem;">
                <div class="text-left text-readable" style="font-size: small">
                  <TransitionGroup name="list" tag="div" style="position: relative">
                      <div v-for="item in calculation" :key="item.key" class="d-flex justify-content-between align-items-center mb-1 w-100 flex-wrap">
                        <small class="mr-4">{{ item.title }}</small>
                        <div>{{item.amount | formatCurrency(invoice.currency)}}</div>
                      </div>
                  </TransitionGroup>
                </div>
                <div class="text-right px-2 flex-grow-1">
                  <small>Estimated payout</small>
                  <br/>
                  <div style="font-size: larger">{{ salary.total | formatCurrency(invoice.currency) }}</div>
                </div>
              </div>
            </h4>

            <slot></slot>
        </div>
    </div>
</template>

<script>
  export default {
    name: "InvoiceSummary",
    props: {
      invoice: {
        type: Object,
        required: true,
      },
      company: {
        type: Object,
        required: false,
      },
      employeeFee: {
        type: Number,
        default: 31.42,
      },
      quickPayFee: {
        type: Number,
        default: 5,
      },
      useQuickPay: {
        type: Boolean,
        default: false,
      },
    },
    computed: {
      netto() {
        return this.invoice.rows.reduce((a, b) => parseFloat(a) + parseFloat(b.amount || 0), 0);
      },
      brutto() {
        return this.netto + this.vat;
      },
      vat() {
        return this.invoice.rows.reduce((a, b) => {
          const carry = parseFloat(a);
          const amount = parseFloat(b.amount || 0);
          const vat = parseFloat(b.vat || 0);
          const current = this.getNetto(amount, vat);

          return carry + current;
        }, 0);
      },
      calculation() {
        if (this.salary.total === 0) return [];
        let list = [];
        if (!!this.invoice.charge_fee) {
          list.push({
            key: 'afterChargeFee',
            title: `Charge Fee (${this.invoice.charge_fee} %)`,
            amount: this.netto - this.salary.afterChargeFee,
          })
        }
        if (!!this.quickPayFee && this.useQuickPay) {
          list.push({
            key: 'afterQuickPayFee',
            title: `Quick Pay Fee (${this.quickPayFee.toFixed(2)} %)`,
            amount: this.netto - this.salary.afterQuickPayFee,
          })
        }
        if (!!this.employeeFee) {
          list.push({
            key: 'afterEmployeeFee',
            title: `Social Fees (${this.employeeFee.toFixed(2)} %)`,
            amount: this.salary.afterQuickPayFee - this.salary.afterEmployeeFee,
          })
        }
        if (!!this.invoice.tax) {
          list.push({
            key: 'afterInvoiceTax',
            title: `Invoice Tax (${this.invoice.tax.toFixed(2)} %)`,
            amount: this.salary.afterEmployeeFee - this.salary.afterInvoiceTax,
          })
        }

        return list;
      },
      salary() {
        let chargeFee = this.netto * this.invert(this.invoice.charge_fee);
        let quickPayFee = this.useQuickPay ? this.netto * this.invert(this.quickPayFee) : this.netto;
        // quickPayFee is merged with serviceFee so 5% service + 5% quick = 10% rather than two subsequent fees (the code above is so users can see how much is taken off individually)
        let totalServiceFee = parseFloat(this.invoice.charge_fee) + parseFloat(this.useQuickPay ? this.quickPayFee : 0);
        let totalFee = this.netto * this.invert(totalServiceFee)
        let employeeFee = totalFee / this.employeeFeeDivider;
        let taxPercentage = this.invert(this.invoice.tax);
        let invoiceTax = employeeFee * taxPercentage;

        return {
          totalServiceFee: totalServiceFee,
          afterChargeFee: chargeFee,
          afterQuickPayFee: quickPayFee,
          afterEmployeeFee: employeeFee,
          afterTotalFee: totalFee,
          afterInvoiceTax: invoiceTax,
          total: Math.floor(invoiceTax),
        }
      },
      employeeFeeDivider() {
        return 1 + (this.employeeFee / 100);
      },
    },
    methods: {
      getNetto(amount, vat) {
        return amount * (vat / 100);
      },
      invert(value) {
        return (100 - value) / 100;
      },
    }
  }
</script>

<style scoped>
  .list-move, /* apply transition to moving elements */
  .list-enter-active,
  .list-leave-active {
    transition: all 0.2s ease;
  }

  .list-enter-from,
  .list-enter,
  .list-leave-to {
    opacity: 0;
  }

  /* ensure leaving items are taken out of layout flow so that moving
     animations can be calculated correctly. */
  .list-leave-active {
    opacity: 0;
    position: absolute;
  }
</style>
